import React, { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { ChevronDown, X, DollarSign } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ComboboxCreatable } from '@/components/ui/combobox-creatable'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { medicationUnits, compoundReasons } from './constants'
import {
  getProductOptions,
  getDoseOptions,
  isDoseDisabled,
  getSigOptions,
  getQuantityOptions,
} from './utils'
import type { PrescriptionProduct, SelectOption, RxPadFormValues } from './types'

interface MedicationEntryProps {
  index: number
  form: UseFormReturn<RxPadFormValues>
  productMap: Record<string, PrescriptionProduct>
  programOptions: SelectOption[]
  programs?: any[]
  canRemove: boolean
  onRemove: () => void
}

const MedicationEntry: React.FC<MedicationEntryProps> = ({
  index,
  form,
  productMap,
  programOptions,
  programs,
  canRemove,
  onRemove,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const { watch, setValue, formState: { errors } } = form

  const medicationKey = watch(`medications.${index}.medication`)
  const programId = watch(`medications.${index}.program`)
  const reason = watch(`medications.${index}.reason`)

  const medErrors = errors.medications?.[index]

  const allMedicationOptions = getProductOptions(productMap, null, programs)
  const medicationOptions = getProductOptions(productMap, programId || null, programs)

  // If current value isn't in filtered options but exists in the full list,
  // add it so the ComboboxCreatable can display its label properly
  const effectiveMedicationOptions = React.useMemo(() => {
    if (!medicationKey) return medicationOptions
    const inFiltered = medicationOptions.some((o) => o.value === medicationKey)
    if (inFiltered) return medicationOptions
    const fromAll = allMedicationOptions.find((o) => o.value === medicationKey)
    if (fromAll) return [fromAll, ...medicationOptions]
    // Fallback: if the key is in productMap, build an option from it
    const product = productMap[medicationKey]
    if (product) {
      return [
        { label: product.dose ? `${product.medication_name} ${product.dose}` : product.medication_name, value: product.key },
        ...medicationOptions,
      ]
    }
    return medicationOptions
  }, [medicationKey, medicationOptions, allMedicationOptions, productMap])
  const doseOptions = getDoseOptions(productMap, medicationKey)
  const doseDisabled = isDoseDisabled(productMap, medicationKey)
  const sigOptions = getSigOptions(productMap, medicationKey)
  const quantityOptions = getQuantityOptions(productMap, medicationKey)

  const reasonOptions = compoundReasons.map((r) => ({
    label: r.reason,
    value: r.reason,
  }))

  const additionalContextOptions = React.useMemo(() => {
    const matched = compoundReasons.find((r) => r.reason === reason)
    if (!matched?.subReasons) return []
    const subs = Array.isArray(matched.subReasons) ? matched.subReasons : [matched.subReasons]
    return subs.map((s) => ({ label: String(s), value: String(s) }))
  }, [reason])

  const unitOptions = medicationUnits.map((u) => ({
    label: u.label,
    value: u.label,
  }))

  // Resolve a unit_of_measure value — might be a CDISC code or a label
  const resolveUnitLabel = (unitValue: string | null | undefined): string => {
    if (!unitValue) return ''
    // Check if it's a known CDISC code
    const byCode = medicationUnits.find((u) => u.value === unitValue)
    if (byCode) return byCode.label
    // Check if it's already a label
    const byLabel = medicationUnits.find((u) => u.label === unitValue)
    if (byLabel) return byLabel.label
    // Return as-is (custom value)
    return unitValue
  }

  const handleMedicationChange = (value: string) => {
    setValue(`medications.${index}.medication`, value, { shouldValidate: true })

    const product = productMap[value]
    if (product) {
      if (product.dose) {
        setValue(`medications.${index}.dose`, product.dose, { shouldValidate: true })
      }
      setValue(`medications.${index}.unit`, resolveUnitLabel(product.unit_of_measure), { shouldValidate: true })
      // Only set compounding reason if the field is currently empty (don't overwrite user edits)
      const currentReason = watch(`medications.${index}.reason`)
      if (product.compounding_reason && !currentReason) {
        setValue(`medications.${index}.reason`, product.compounding_reason, { shouldValidate: true })
      }
    } else {
      setValue(`medications.${index}.dose`, '')
      setValue(`medications.${index}.unit`, '')
    }
    setValue(`medications.${index}.sig`, '')
    setValue(`medications.${index}.quantity`, '')
  }

  const handleProgramChange = (value: string) => {
    setValue(`medications.${index}.program`, value)
    setValue(`medications.${index}.medication`, '')
    setValue(`medications.${index}.dose`, '')
    setValue(`medications.${index}.sig`, '')
    setValue(`medications.${index}.quantity`, '')
    setValue(`medications.${index}.unit`, '')
    setValue(`medications.${index}.reason`, '')
  }

  const selectedProduct = medicationKey ? productMap[medicationKey] : null
  const selectedPrice = Number(selectedProduct?.price) || 0
  const quantity = watch(`medications.${index}.quantity`)
  const totalPrice = selectedPrice * (Number(quantity) || 1)

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-900">
            Medication {index + 1}
          </h3>
          {selectedProduct && selectedPrice > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-700">
              <DollarSign className="h-3 w-3" />
              {totalPrice.toFixed(2)}
            </span>
          )}
        </div>
        {canRemove && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRemove}
            className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            Remove
          </Button>
        )}
      </div>

      {/* Program */}
      <div className="mb-4">
        <Label className="mb-1.5 block text-xs font-medium text-slate-500">Program</Label>
        <Select
          value={programId || undefined}
          onValueChange={handleProgramChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select program" />
          </SelectTrigger>
          <SelectContent>
            {programOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Medication */}
      <div className="mb-4">
        <Label className="mb-1.5 block text-xs font-medium text-slate-500">
          Medication <span className="text-red-500">*</span>
        </Label>
        <ComboboxCreatable
          options={effectiveMedicationOptions}
          value={medicationKey || ''}
          onChange={handleMedicationChange}
          onBlur={() => form.trigger(`medications.${index}.medication`)}
          placeholder="Select medication"
          allowCustomValue={false}
        />
        {medErrors?.medication && (
          <p className="mt-1 text-xs text-red-500">{medErrors.medication.message}</p>
        )}
      </div>

      {/* Dose + Refills */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-slate-500">
            Dose <span className="text-red-500">*</span>
          </Label>
          {doseDisabled ? (
            <Input
              value={watch(`medications.${index}.dose`) || ''}
              disabled
              className="bg-[#f0f0f0]"
            />
          ) : (
            <ComboboxCreatable
              options={doseOptions}
              value={watch(`medications.${index}.dose`) || ''}
              onChange={(v) => setValue(`medications.${index}.dose`, v)}
              onBlur={() => form.trigger(`medications.${index}.dose`)}
              placeholder="Type dose"
            />
          )}
          {medErrors?.dose && (
            <p className="mt-1 text-xs text-red-500">{medErrors.dose.message}</p>
          )}
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-slate-500">
            Refills <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            min={0}
            value={watch(`medications.${index}.refills`) || ''}
            onChange={(e) => {
              if (e.target.value === '' || /^[0-9]+$/.test(e.target.value)) {
                setValue(`medications.${index}.refills`, e.target.value)
              }
            }}
            onBlur={() => form.trigger(`medications.${index}.refills`)}
            placeholder="e.g. 5"
          />
          {medErrors?.refills && (
            <p className="mt-1 text-xs text-red-500">{medErrors.refills.message}</p>
          )}
        </div>
      </div>

      {/* Quantity + Unit of Measure */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-slate-500">
            Quantity <span className="text-red-500">*</span>
          </Label>
          <ComboboxCreatable
            options={quantityOptions}
            value={watch(`medications.${index}.quantity`) || ''}
            onChange={(v) => {
              const intOnly = v.replace(/[^0-9]/g, '');
              setValue(`medications.${index}.quantity`, intOnly);
            }}
            onBlur={() => form.trigger(`medications.${index}.quantity`)}
            placeholder="Type quantity"
            inputFilter={/[^0-9]/g}
          />
          {medErrors?.quantity && (
            <p className="mt-1 text-xs text-red-500">{medErrors.quantity.message}</p>
          )}
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-slate-500">
            Unit of Measure <span className="text-red-500">*</span>
          </Label>
          <ComboboxCreatable
            options={unitOptions}
            value={watch(`medications.${index}.unit`) || ''}
            onChange={(v) => setValue(`medications.${index}.unit`, v)}
            onBlur={() => form.trigger(`medications.${index}.unit`)}
            placeholder="Select unit"
          />
          {medErrors?.unit && (
            <p className="mt-1 text-xs text-red-500">{medErrors.unit.message}</p>
          )}
        </div>
      </div>

      {/* Instructions (Sig) */}
      <div className="mb-4">
        <Label className="mb-1.5 block text-xs font-medium text-slate-500">
          Instructions <span className="text-red-500">*</span>
        </Label>
        <ComboboxCreatable
          options={sigOptions}
          value={watch(`medications.${index}.sig`) || ''}
          onChange={(v) => setValue(`medications.${index}.sig`, v)}
          onBlur={() => form.trigger(`medications.${index}.sig`)}
          placeholder="Type instruction"
        />
        {medErrors?.sig && (
          <p className="mt-1 text-xs text-red-500">{medErrors.sig.message}</p>
        )}
      </div>

      {/* Reason for Compounding */}
      <div className="mb-4">
        <Label className="mb-1.5 block text-xs font-medium text-slate-500">
          Reason for compounding <span className="text-red-500">*</span>
        </Label>
        <ComboboxCreatable
          options={reasonOptions}
          value={watch(`medications.${index}.reason`) || ''}
          onChange={(v) => setValue(`medications.${index}.reason`, v)}
          onBlur={() => form.trigger(`medications.${index}.reason`)}
          placeholder="Select reason"
        />
        {medErrors?.reason && (
          <p className="mt-1 text-xs text-red-500">{medErrors.reason.message}</p>
        )}
      </div>

      {/* Collapsible Additional Details */}
      <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isDetailsOpen ? 'rotate-180' : ''}`}
            />
            Additional Details
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-4">
          {/* Additional Context */}
          {additionalContextOptions.length > 0 && (
            <div>
              <Label className="mb-1.5 block text-xs font-medium text-slate-500">
                Additional Context
              </Label>
              <ComboboxCreatable
                options={additionalContextOptions}
                value={watch(`medications.${index}.additionalContext`) || ''}
                onChange={(v) => setValue(`medications.${index}.additionalContext`, v)}
                placeholder="Select context"
              />
            </div>
          )}

          {/* Days Supply + Flavor */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label className="mb-1.5 block text-xs font-medium text-slate-500">
                Days Supply
              </Label>
              <Input
                type="number"
                min={1}
                value={watch(`medications.${index}.daysSupply`) || ''}
                onChange={(e) => setValue(`medications.${index}.daysSupply`, e.target.value)}
                placeholder="e.g. 30"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-medium text-slate-500">Flavor</Label>
              <Input
                value={watch(`medications.${index}.flavor`) || ''}
                onChange={(e) => setValue(`medications.${index}.flavor`, e.target.value)}
                placeholder="e.g. Cherry"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-slate-500">Notes</Label>
            <Textarea
              value={watch(`medications.${index}.notes`) || ''}
              onChange={(e) => setValue(`medications.${index}.notes`, e.target.value)}
              placeholder="Additional notes..."
              rows={3}
            />
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {[
              { field: 'doNotFill' as const, label: 'Do Not Fill' },
              { field: 'refillNeeded' as const, label: 'Refill As Needed' },
              { field: 'doNotRefill' as const, label: 'Do Not Refill' },
              { field: 'patientContactRequested' as const, label: 'Patient Contact Requested' },
            ].map(({ field, label }) => (
              <div key={field} className="flex items-center gap-2.5">
                <Checkbox
                  id={`medications.${index}.${field}`}
                  checked={watch(`medications.${index}.${field}`) || false}
                  onCheckedChange={(checked) =>
                    setValue(`medications.${index}.${field}`, checked === true)
                  }
                />
                <Label
                  htmlFor={`medications.${index}.${field}`}
                  className="text-sm font-normal text-slate-700 cursor-pointer"
                >
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

export default MedicationEntry
